import React, { useEffect, useState } from "react";
import { Avatar, Descriptions, Spin, Tag } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import {
  deleteUserById,
  fetchAllUsers,
  getUserById,
} from "../../app/userSlice";
import { UserResponse } from "../../types/taskTypes";
import { useNavigate } from "react-router-dom";
import { hasPermission } from "../../utils/permission";
import { toast } from "react-toastify";
import CommonModal from "../../components/CommonModal";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import { useDebounce } from "use-debounce";
import Swal from "sweetalert2";
import SortIcons from "../../components/SortIcons/SortIcons";

const CoreTeamList = () => {
  const total = useSelector((state: RootState) => state.user.total);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pagesize] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);
  const [userList, setUserList] = useState<UserResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserResponse | null>(null);
  const [orderBy, setOrderBy] = useState<
    | "id"
    | "first_name"
    | "last_name"
    | "email"
    | "mobile"
    | "created_at"
    | "status"
  >("id");
  const [orderDir, setOrderDir] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        const result = await dispatch(
          fetchAllUsers({
            page,
            pagesize,
            search: debouncedSearch,
            order_by: orderBy,
            order_dir: orderDir,
          })
        ).unwrap();
        setUserList(result.data?.data || []);
        //toast.success(result.message || "User List Fetched Successfully");
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [dispatch, page, pagesize, debouncedSearch, orderBy, orderDir]);

  const handleDelete = async (id: number) => {
    try {
      const result = await dispatch(deleteUserById(id)).unwrap();
      setUserList((prevList) => prevList.filter((user) => user.id !== id));
      setPage(1);
      return result.message;
    } catch (error: any) {
      toast.error(error || "Failed to delete Core Team");
    }
  };
  const handleSort = (field: typeof orderBy) => {
    if (orderBy === field) {
      setOrderDir((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setOrderBy(field);
      setOrderDir("desc");
    }
    setPage(1);
  };
  return (
    <>
      <Breadcrumb pageName="Manage User" />
      <div className="rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-md dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:pb-1">
        <div className="max-w-full overflow-x-auto">
          {/* Search Bar */}
          <div className="flex justify-end mb-4">
            <input
              type="text"
              placeholder="Search by name, email, or mobile"
              value={search}
              onChange={(e) => {
                const value = e.target.value.replace(/^\s+/, "");
                setSearch(value);
                setPage(1);
              }}
              className="w-full sm:w-1/3 rounded-md border border-gray-300 px-4 py-2 text-sm shadow-sm transition-all focus:border-blue-500 focus:ring focus:ring-blue-200 focus:outline-none"
            />
          </div>
          <table className="w-full table-auto sm:table-fixed">
            <thead>
              <tr className="bg-gray-2 text-left dark:bg-meta-4">
                <th className="py-4 px-4 text-left w-[50px] whitespace-nowrap">
                  Sr No
                </th>
                <th
                  className="py-4 px-4 text-left w-[120px] whitespace-nowrap cursor-pointer"
                  onClick={() => handleSort("first_name")}
                >
                  <div className="flex items-center gap-1">
                    <span>First Name</span>
                    <SortIcons
                      isActive={orderBy === "first_name"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left w-[120px] whitespace-nowrap cursor-pointer"
                  onClick={() => handleSort("last_name")}
                >
                  <div className="flex items-center gap-1">
                    <span>Last Name</span>
                    <SortIcons
                      isActive={orderBy === "last_name"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left w-[250px] whitespace-nowrap cursor-pointer"
                  onClick={() => handleSort("email")}
                >
                  <div className="flex items-center gap-1">
                    <span>Email</span>
                    <SortIcons
                      isActive={orderBy === "email"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left w-[200px] whitespace-nowrap cursor-pointer"
                  onClick={() => handleSort("mobile")}
                >
                  <div className="flex items-center gap-1">
                    <span>Mobile Number</span>
                    <SortIcons
                      isActive={orderBy === "mobile"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left w-[100px] whitespace-nowrap cursor-pointer"
                  onClick={() => handleSort("status")}
                >
                  <div className="flex items-center gap-1">
                    <span>Status</span>
                    <SortIcons
                      isActive={orderBy === "status"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th
                  className="py-4 px-4 text-left w-[120px] whitespace-nowrap cursor-pointer"
                  onClick={() => handleSort("created_at")}
                >
                  <div className="flex items-center gap-1">
                    <span>Registered On</span>
                    <SortIcons
                      isActive={orderBy === "created_at"}
                      direction={orderDir}
                    />
                  </div>
                </th>
                <th className="py-4 px-4 w-[100px] text-left whitespace-nowrap">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-6">
                    <Spin />
                  </td>
                </tr>
              ) : userList.length > 0 ? (
                userList.map((user, index: number) => (
                  <tr key={user.id}>
                    <td className="border-b py-4 px-4 ">
                      {(page - 1) * pagesize + index + 1}
                    </td>
                    <td className="border-b py-4 px-4 ">
                      {user.first_name || "--"}
                    </td>
                    <td className="border-b py-4 px-4 ">
                      {user.last_name || "--"}
                    </td>
                    <td className="border-b py-4 px-4 whitespace-nowrap">
                      {user.email || "--"}
                    </td>
                    <td className="border-b py-4 px-4 whitespace-nowrap">
                      {user.mobile && user.mobile !== "null"
                        ? user.mobile.startsWith(user.country_code)
                          ? user.mobile
                          : `${user.country_code} ${user.mobile}`
                        : "--"}
                    </td>

                    <td className="border-b py-4 px-4 ">
                      <Tag
                        color={
                          user.status?.toLowerCase() === "active"
                            ? "green"
                            : user.status?.toLowerCase() === "inactive"
                            ? "red"
                            : "default"
                        }
                        className="uppercase font-medium"
                      >
                        {user.status ? user.status.toUpperCase() : "--"}
                      </Tag>
                    </td>
                    <td className="border-b py-4 px-4 ">
                      {user.created_at
                        ? new Date(user.created_at).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                        : "--"}
                    </td>
                    <td className="border-b py-4 px-4 ">
                      <div className="flex items-center space-x-3.5">
                        {hasPermission("user-view") && (
                          <button
                            onClick={async () => {
                              try {
                                const result = await dispatch(
                                  getUserById(user.id)
                                ).unwrap();
                                setSelectedUser(result?.data || null); // ✅ safe fallback to null
                                setIsModalVisible(true);
                              } catch (err) {
                                toast.error("Failed to load user details");
                              }
                            }}
                            className="hover:text-primary text-base"
                          >
                            <EyeOutlined />
                          </button>
                        )}
                        {hasPermission("user-update") && (
                          <button
                            onClick={() =>
                              navigate(`/dashboard/updateUser`, {
                                state: {
                                  id: user.id,
                                },
                              })
                            }
                            className="hover:text-green-600 text-base"
                          >
                            <EditOutlined />
                          </button>
                        )}

                        {hasPermission("user-delete") && (
                          <button
                            className="hover:text-red-500"
                            onClick={async () => {
                              const result = await Swal.fire({
                                title:
                                  "Are you sure you want to delete this user?",
                                text: "You won't be able to revert this!",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonColor: "#d33",
                                cancelButtonColor: "#3085d6",
                                confirmButtonText: "Yes, delete it!",
                              });

                              if (result.isConfirmed) {
                                try {
                                  const message = await handleDelete(user.id); // should return a success message

                                  await Swal.fire({
                                    title: "Deleted!",
                                    text: message || "User has been deleted.",
                                    icon: "success",
                                    timer: 1500,
                                    showConfirmButton: false,
                                  });

                                  // Optional: refresh user list or update UI here
                                } catch (err: any) {
                                  await Swal.fire({
                                    title: "Error",
                                    text:
                                      err?.message || "Failed to delete user",
                                    icon: "error",
                                  });
                                }
                              }
                            }}
                          >
                            <DeleteOutlined />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="text-center font-medium py-4">
                    No result found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {userList && userList.length > 0 && (
            <div className="flex justify-between items-center mt-4 mb-2 flex-wrap gap-2">
              <div className="text-sm text-gray-600">
                Showing {(page - 1) * pagesize + 1} to{" "}
                {Math.min(page * pagesize, total)} of {total} entries
              </div>

              <div className="flex items-center gap-1 flex-wrap">
                {/* Previous Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page === 1
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                >
                  &lt;
                </button>

                {/* Page Numbers with Ellipsis */}
                {[...Array(Math.ceil(total / pagesize)).keys()]
                  .map((n: number) => n + 1)
                  .filter((p: number) => {
                    return (
                      p === 1 ||
                      p === Math.ceil(total / pagesize) ||
                      (p >= page - 1 && p <= page + 1)
                    );
                  })
                  .reduce((acc: (number | string)[], val: number, i, arr) => {
                    if (i > 0 && val - (arr[i - 1] as number) > 1) {
                      acc.push("...");
                    }
                    acc.push(val);
                    return acc;
                  }, [])
                  .map((p: number | string, idx: number) =>
                    typeof p === "string" ? (
                      <span
                        key={`ellipsis-${idx}`}
                        className="px-3 py-1.5 text-sm text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`px-3 py-1.5 text-sm rounded-md border ${
                          p === page
                            ? "bg-gray-300 text-gray-800"
                            : "hover:bg-blue-100 text-blue-600 border-blue-300"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                {/* Next Button */}
                <button
                  className={`px-3 py-1.5 text-sm rounded-md border ${
                    page * pagesize >= total
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "hover:bg-blue-100 text-blue-600 border-blue-300"
                  }`}
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page * pagesize >= total}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>

        <CommonModal
          visible={isModalVisible}
          onClose={() => {
            setIsModalVisible(false);
            setSelectedUser(null);
          }}
          title={
            <div className="flex items-center gap-1">
              <Avatar icon={<UserOutlined />} />
              <span className="font-semibold text-gray-800">User Details</span>
            </div>
          }
        >
          {!selectedUser ? (
            <div className="flex justify-center items-center h-32">
              <Spin />
            </div>
          ) : (
            <Descriptions
              column={1}
              bordered
              size="middle"
              labelStyle={{
                fontWeight: 600,
                backgroundColor: "#f9f9f9",
                width: "160px",
              }}
              contentStyle={{ backgroundColor: "#fff", color: "#262626" }}
            >
              {selectedUser?.unique_code && (
                <Descriptions.Item label="Unique Code">
                  {selectedUser.unique_code}
                </Descriptions.Item>
              )}
              {selectedUser?.first_name && (
                <Descriptions.Item label="First Name">
                  {selectedUser.first_name}
                </Descriptions.Item>
              )}
              {selectedUser?.last_name && (
                <Descriptions.Item label="Last Name">
                  {selectedUser.last_name}
                </Descriptions.Item>
              )}
              {selectedUser?.email && (
                <Descriptions.Item label="Email Address">
                  {selectedUser.email}
                </Descriptions.Item>
              )}
              {selectedUser?.mobile && selectedUser?.mobile !== "null" && (
                <Descriptions.Item label="Mobile Number">
                  {selectedUser.country_code + " " + selectedUser.mobile}
                </Descriptions.Item>
              )}
              {selectedUser?.gender && (
                <Descriptions.Item label="Gender">
                  {selectedUser.gender.charAt(0).toUpperCase() +
                    selectedUser.gender.slice(1)}
                </Descriptions.Item>
              )}
              {selectedUser?.dob && (
                <Descriptions.Item label="Date1 of Birth">
                  {selectedUser.dob
                    ? new Date(selectedUser.dob).toLocaleDateString("en-GB") // ✅ 'en-GB' gives dd/mm/yyyy
                    : "--"}
                </Descriptions.Item>
              )}
              {selectedUser?.designation !== "" && (
                <Descriptions.Item label="Designation">
                  {selectedUser.designation}
                </Descriptions.Item>
              )}
              {selectedUser?.emergency_contact !== "" && (
                <Descriptions.Item label="Emergency Contact">
                  {selectedUser.emergency_contact_country_code +
                    " " +
                    selectedUser.emergency_contact}
                </Descriptions.Item>
              )}
              {selectedUser?.third_party_name !== "" && (
                <Descriptions.Item label="Third Party Name">
                  {selectedUser.third_party_name}
                </Descriptions.Item>
              )}
              {selectedUser?.status && (
                <Descriptions.Item label="Status">
                  <Tag
                    color={
                      selectedUser.status.toLowerCase() === "active"
                        ? "green"
                        : selectedUser.status.toLowerCase() === "inactive"
                        ? "red"
                        : "default"
                    }
                    className="uppercase font-medium"
                  >
                    {selectedUser.status.toUpperCase()}
                  </Tag>
                </Descriptions.Item>
              )}
              {selectedUser?.roles?.[0]?.display_name && (
                <Descriptions.Item label="Role">
                  {selectedUser.roles[0].display_name}
                </Descriptions.Item>
              )}
            </Descriptions>
          )}
        </CommonModal>
      </div>
    </>
  );
};

export default CoreTeamList;
